import React, { useMemo, useState } from 'react';

import { Button } from '../../Button';
import { Box } from '../../Box';
import { Dialog, DialogContent } from '../../Dialog';
import { ImageCropper } from '../../ImageCropper';
import { FileInput, FileInputProps, FileInputValue } from '../FileInput';
import { Typography } from '../../Typography';

import { useStyles } from './FileInputWithCrop.styles';

export const FileInputWithCrop = ({
  onChange,
  cache,
  validators,
  ...rest
}: FileInputProps) => {
  const { classes } = useStyles();
  const [cropModalState, setCropModalState] = useState<boolean>(false);
  const [blobDocument, setBlobDocument] = useState<FileInputValue | undefined>(
    undefined,
  );
  const [croppedResult, setCroppedResult] = useState<Blob | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  const handleCropModalState =
    (state: boolean, reset = true) =>
    () => {
      setCropModalState(state);
      if (!state && reset) {
        setCroppedResult(null);
        setBlobDocument(undefined);
        onChange(undefined);
      }
    };

  const handleCropComplete = (croppedBlob: Blob) => {
    setCroppedResult(croppedBlob);
  };

  const convertToFormat = (blob: Blob, mimeType: string): Promise<Blob> => {
    return new Promise((resolve) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();

      img.onload = () => {
        canvas.width = img.width;
        canvas.height = img.height;
        ctx?.drawImage(img, 0, 0);
        canvas.toBlob(
          (resultBlob) => {
            resolve(resultBlob || blob);
          },
          mimeType,
          0.9,
        );
      };

      img.src = URL.createObjectURL(blob);
    });
  };

  const handleApplyCrop = async () => {
    if (!croppedResult) return;

    setLoading(true);

    let originalFileName = 'image';
    let originalFileType = 'image/jpeg';

    if (
      blobDocument &&
      blobDocument.source &&
      typeof blobDocument.source !== 'string' &&
      blobDocument.source instanceof File
    ) {
      const fileObj = blobDocument.source;

      originalFileType = fileObj.type || 'image/jpeg';
      if (fileObj.name) {
        originalFileName = fileObj.name;
      }
    }

    let extension = '';

    if (originalFileType && originalFileType.includes('/')) {
      extension = originalFileType.split('/')[1];
    }

    let fileNameWithExt = originalFileName;
    if (originalFileName.lastIndexOf('.') > 0) {
      fileNameWithExt = `${originalFileName.substring(
        0,
        originalFileName.lastIndexOf('.'),
      )}.${extension}`;
    } else {
      fileNameWithExt = `${originalFileName}.${extension}`;
    }

    const targetMimeType = originalFileType;

    const resultBlob = await convertToFormat(croppedResult, targetMimeType);

    const file = new File([resultBlob], fileNameWithExt, {
      type: targetMimeType,
    });

    if (cache) {
      try {
        const { file_id: fileId, expired_at: expiredAt } = await cache(file);
        onChange({
          source: file,
          value: JSON.stringify({
            type: 'file_cache',
            id: fileId,
            expiredAt: expiredAt,
          }),
        });
        setLoading(false);
        handleCropModalState(false, false)();
      } catch (error) {
        setLoading(false);
        handleCropModalState(false)();
        return;
      }
    } else {
      handleCropModalState(false, false)();
      return;
    }
  };

  const handleOnChange = async (value: FileInputValue | undefined) => {
    onChange(value);
    setBlobDocument(value);

    if (validators && (await validators(value))) return;

    if (
      value &&
      value.source &&
      typeof value.source !== 'string' &&
      value.source instanceof File
    ) {
      const fileSource = value.source as File;
      fileSource.arrayBuffer().then((buffer) => {
        const blob = new Blob([buffer], { type: fileSource.type });
        setCroppedResult(blob);
      });
      handleCropModalState(true)();
    }
  };

  const formattedBlob = useMemo(() => {
    return new Blob(
      [
        JSON.stringify({
          url: URL.createObjectURL(
            blobDocument?.source instanceof File
              ? blobDocument.source
              : new Blob(),
          ),
        }),
      ],
      { type: 'application/json' },
    );
  }, [blobDocument?.source]);

  return (
    <Box>
      <FileInput
        {...rest}
        onChange={handleOnChange}
        variant="input"
        validators={validators}
      />

      <Dialog open={cropModalState} onClose={handleCropModalState(false)}>
        <DialogContent
          onClose={handleCropModalState(false)}
          actions={
            <>
              <Button
                variant="tertiaryLight"
                size="large"
                onClick={handleCropModalState(false)}
              >
                Отменить
              </Button>
              <Button
                variant="primary"
                size="large"
                loading={loading}
                disabled={loading}
                onClick={handleApplyCrop}
              >
                Сохранить
              </Button>
            </>
          }
        >
          <Box className={classes.actions}>
            <Typography variant="h5">Кадрирование обложки</Typography>
            <Typography variant="body1" color="text.light.secondary">
              Выделите нужную область изображения
            </Typography>
            <Box className={classes.cropContainer}>
              <ImageCropper
                blob={formattedBlob}
                onCropComplete={handleCropComplete}
              />
            </Box>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
};
